/* Wasteland Run — world constants, worm evolution tiers, suspend schema, fresh run state */
import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const DEVOURED_BASELINE = 666;

export const W = 960;
export const H = 420;
export const GROUND = 356;
export const GRAVITY = 0.62;
export const JUMP_V = -11.4;

// the longer a runner survives, the worse the thing chasing them gets
export const WORM_TIERS = [
  { src: "/assets/worm-sprite.png", name: "STALKER", h: 172, at: 0, pressure: 0.055 },
  { src: "/assets/worm-ravager.png", name: "RAVAGER", h: 192, at: 350, pressure: 0.075 },
  { src: "/assets/worm-magma.png", name: "MAGMA", h: 210, at: 800, pressure: 0.098 },
  { src: "/assets/worm-apex.png", name: "APEX DEVOURER", h: 230, at: 1400, pressure: 0.125 },
];

// delivery route: every 30,000 PTS completes a delivery; 20 deliveries clear the game.
// each delivery upgrades the boombox — missiles, larger fireballs, heavier plating damage
export const DELIVERY_TARGET = 30000;
export const DELIVERY_MAX = 20;
// weapon upgrades: every 10,000 PTS the boombox levels up — heavier plating damage,
// larger fireballs, faster cycling, missile chassis from MK-3 — kills come quicker as the run heats up
// weapon crates: rare supply drops grant a temporary MK overdrive beyond the current level
export const WEAPON_CRATE_BOOST = 3;
export const WEAPON_CRATE_SECONDS = 20;
export const weaponLevel = (g) => {
  const base = Math.min(20, 1 + Math.floor((Math.floor(g.distance) + g.credits * 25) / 10000));
  return g.weaponBoost > 0 ? Math.min(24, base + WEAPON_CRATE_BOOST) : base;
};
export const shotPower = (g) => Math.min(5, weaponLevel(g));
export const shotScale = (g) => Math.min(2.5, 1 + (weaponLevel(g) - 1) * 0.09);
export const shotCooldown = (g) => Math.max(8, 17 - weaponLevel(g));

// collectible arsenal — crimson supply cases drop mid-run carrying weapons; once
// secured, a weapon is selectable anytime from the arsenal (persisted per browser)
export const RANGED_WEAPONS = [
  { id: "boombox", name: "BOOMBOX", desc: "Standard bass rounds — levels up every 10,000 PTS" },
  { id: "machinegun", name: "MACHINE GUN BOOMBOX", desc: "Rapid-fire chassis — lighter slugs at nearly double the cycle speed" },
  { id: "grenades", name: "GRENADE CASSETTES", desc: "Arcing cassettes with a splash blast — heavy worm damage" },
];
export const MELEE_WEAPONS = [
  { id: "sword", name: "SWORD", desc: "Quick arc slash" },
  { id: "laser", name: "LASER SWORD", desc: "Long reach, double plating damage" },
];
export const DROP_SEQUENCE = ["laser", "machinegun", "grenades"];
export const nextLockedWeapon = (owned) => DROP_SEQUENCE.find((id) => !owned.includes(id)) || null;
export const weaponName = (id) => [...RANGED_WEAPONS, ...MELEE_WEAPONS].find((w) => w.id === id)?.name || id;
export const ARSENAL_KEY = "wr-arsenal";
export const loadArsenal = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(ARSENAL_KEY) || "{}");
    const known = [...RANGED_WEAPONS, ...MELEE_WEAPONS].map((w) => w.id);
    const owned = [...new Set(["boombox", "sword", ...(Array.isArray(parsed.owned) ? parsed.owned : [])])].filter((id) => known.includes(id));
    return {
      owned,
      ranged: owned.includes(parsed.ranged) && RANGED_WEAPONS.some((w) => w.id === parsed.ranged) ? parsed.ranged : "boombox",
      melee: owned.includes(parsed.melee) && MELEE_WEAPONS.some((w) => w.id === parsed.melee) ? parsed.melee : "sword",
    };
  } catch {
    return { owned: ["boombox", "sword"], ranged: "boombox", melee: "sword" };
  }
};
export const saveArsenal = (arsenal) => localStorage.setItem(ARSENAL_KEY, JSON.stringify(arsenal));

// server sync: weapons follow the Titan Tag across devices. Saves union-merge
// server-side, so a stale device can never wipe a secured weapon.
export const mergeArsenal = (local, server) => ({
  owned: [...new Set([...(local?.owned || []), ...(server?.owned || [])])],
  ranged: server?.ranged || local?.ranged || "boombox",
  melee: server?.melee || local?.melee || "sword",
});
export const fetchServerArsenal = async (tag) => {
  const { data } = await axios.get(`${API}/wasteland/arsenal`, { params: { tag } });
  return data;
};
export const pushServerArsenal = (tag, arsenal) => {
  if (!tag) return;
  axios.post(`${API}/wasteland/arsenal`, { tag, ...arsenal }).catch(() => {});
};
// boss wave: every 5th delivery round the Apex Devourer hunts enraged until the delivery lands
export const BOSS_PRESSURE = 0.17;
export const isBossWave = (g) => g.delivery % 5 === 0;

// boss worms: every OTHER delivery round complete (2nd, 4th, 6th...) a named boss worm
// erupts ahead of Dolo for a stand-up duel. 10,000 hull — far too thick for standard
// issue, so the arsenal jumps to BOSS LEVEL (damage x40) for as long as the fight runs.
// the chaser hangs back and the field clears; the boss slams rolling shockwaves that
// must be jumped. five user-supplied boss artworks cycle in order.
export const BOSS_WORM_HP = 10000;
export const BOSS_WEAPON_MULT = 40;
export const isWormBossRound = (g) => g.delivery % 2 === 0;
export const BOSS_WORMS = [
  {
    src: "/assets/boss-worm-1-idle.png",
    name: "SKULLMAW", sfx: { roar: "boss-roar-1", slam: "boss-slam-1" },
    h: 300,
    poses: {
      idle: "/assets/boss-worm-1-idle.png",
      roar: "/assets/boss-worm-1-roar.png",
      attack: "/assets/boss-worm-1-attack.png",
      damage: "/assets/boss-worm-1-damage.png",
      death: "/assets/boss-worm-1-death.png",
    },
  },
  {
    src: "/assets/boss-worm-2-idle.png",
    name: "CINDERJAW", sfx: { roar: "boss-roar-2", slam: "boss-slam-2" },
    h: 280,
    poses: {
      idle: "/assets/boss-worm-2-idle.png",
      roar: "/assets/boss-worm-2-roar.png",
      attack: "/assets/boss-worm-2-attack.png",
      damage: "/assets/boss-worm-2-damage.png",
      death: "/assets/boss-worm-2-death.png",
    },
  },
  {
    src: "/assets/boss-worm-3-idle.png",
    name: "BONEHORN",
    sfx: { roar: "boss-roar-3", slam: "boss-slam-3" },
    h: 300,
    poses: {
      idle: "/assets/boss-worm-3-idle.png",
      roar: "/assets/boss-worm-3-roar.png",
      attack: "/assets/boss-worm-3-attack.png",
      damage: "/assets/boss-worm-3-damage.png",
      death: "/assets/boss-worm-3-death.png",
    },
  },
  {
    src: "/assets/boss-worm-4-idle.png",
    name: "IRONHIDE",
    sfx: { roar: "boss-roar-4", slam: "boss-slam-4" },
    h: 300,
    poses: {
      idle: "/assets/boss-worm-4-idle.png",
      roar: "/assets/boss-worm-4-roar.png",
      attack: "/assets/boss-worm-4-attack.png",
      damage: "/assets/boss-worm-4-damage.png",
      death: "/assets/boss-worm-4-death.png",
    },
  },
  {
    src: "/assets/boss-worm-5-idle.png",
    name: "DOOMGAZE",
    sfx: { roar: "boss-roar-5", slam: "boss-slam-5" },
    h: 300,
    poses: {
      idle: "/assets/boss-worm-5-idle.png",
      roar: "/assets/boss-worm-5-roar.png",
      attack: "/assets/boss-worm-5-attack.png",
      damage: "/assets/boss-worm-5-damage.png",
      death: "/assets/boss-worm-5-death.png",
    },
  },
];

// mutated chargers — user-supplied run cycles sliced into transparent strips.
// the wolf darts in fast and folds to a single hit; the horned bear is a slower,
// heavier bully that soaks three hits and pays a much bigger bounty. Both charge
// head-on at ground level and sit out boss waves.
export const BEAST_ART = {
  wolf: { src: "/assets/beast-wolf-strip.png", frames: 19, fw: 217, fh: 139, w: 120, hp: 1, speed: 2.2, credits: 4, wormHit: 45, animEvery: 3 },
  bear: { src: "/assets/beast-bear-strip.png", frames: 30, fw: 139, fh: 131, w: 172, hp: 3, speed: 1.6, credits: 12, wormHit: 80, animEvery: 4 },
};

// abandoned paused runs survive a phone call — frozen for 5 minutes
export const SUSPEND_KEY = "wasteland-run-suspend";
export const SUSPEND_TTL = 5 * 60 * 1000;
export const SUSPEND_FIELDS = [
  "frame", "speed", "distance", "credits", "y", "vy", "jumps",
  "invuln", "worm", "tier", "tierFlash", "obstacles", "coins", "nextObstacle",
  "slowmo", "slowmoCooldown", "slowmoArmed",
  "jumpTotal", "boosts", "boost", "boostFlash", "cells",
  "crows", "nextCrow", "crowKills", "skyFlash",
  "beasts", "nextBeast", "beastKills", "beastFlash", "beastSpawns",
  "shots", "slash", "slashCd", "shotCd", "shootAnim", "shootDir", "wormHp", "repels",
  "mesas", "props", "night", "delivery", "runFrames", "bossFlash", "bossShatters", "weaponLevel",
  "weaponCrates", "nextCrate", "weaponBoost", "ranged", "melee", "owned", "weaponDrops", "nextDrop",
  "wormBoss", "wormBossKills", "bossFights", "bossShocks", "bossEngageFlash", "bossKillFlash", "bossKillName",
  "bossVariantKills", "weakCritLanded",
];

/* DAILY CHALLENGE — one fixed course per UTC day. The seed derives from the date
   (djb2, mirrored in the backend), and mulberry32 drives every course decision
   so every runner faces the exact same wasteland today. */
export const dailyKey = (d = new Date()) => d.toISOString().slice(0, 10);

export const seedFromKey = (key) => {
  let h = 5381;
  for (let i = 0; i < key.length; i += 1) h = ((h << 5) + h + key.charCodeAt(i)) >>> 0;
  return h;
};

export const mulberry32 = (seed) => () => {
  seed |= 0;
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

export function freshState(daily = null) {
  return {
    daily, // UTC day key when running the daily challenge course
    rng: daily ? mulberry32(seedFromKey(daily)) : Math.random,
    frame: 0,
    speed: 5,
    distance: 0,
    credits: 0,
    y: GROUND - 118,
    vy: 0,
    jumps: 0,
    jumpTotal: 0,
    boosts: 0,
    boost: 0,
    boostFlash: 0,
    shots: [],
    slash: 0,
    slashCd: 0,
    shotCd: 0,
    shootAnim: 0,
    shootDir: 1,
    wormHp: 5,
    repelFlash: 0,
    repels: 0,
    invuln: 0,
    tubeBoost: null,
    tubeShield: 0,
    worm: 230,
    tier: 0,
    tierFlash: 0,
    slowmo: 0,
    slowmoCooldown: 0,
    slowmoArmed: true,
    night: 0,
    obstacles: [],
    coins: [],
    cells: [],
    crows: [],
    nextCrow: 560,
    crowKills: 0,
    skyFlash: 0,
    beasts: [],
    nextBeast: 260,
    beastKills: 0,
    beastFlash: 0,
    beastSpawns: 0,
    dust: [],
    ash: Array.from({ length: 26 }, () => ({ x: Math.random() * W, y: Math.random() * H, s: 0.4 + Math.random() * 1.2, r: 1 + Math.random() * 2 })),
    skyline: Array.from({ length: 14 }, (_, i) => ({ x: i * 90 + Math.random() * 40, w: 34 + Math.random() * 50, h: 60 + Math.random() * 120 })),
    ruins: Array.from({ length: 9 }, (_, i) => ({ x: i * 140 + Math.random() * 60, w: 50 + Math.random() * 70, h: 30 + Math.random() * 60 })),
    // grand canyon mesas — far parallax layer
    mesas: Array.from({ length: 5 }, (_, i) => ({
      x: i * 250 + Math.random() * 70,
      w: 150 + Math.random() * 90,
      h: 70 + Math.random() * 80,
      c: i % 2 ? "#8a2c10" : "#6e1f0c",
    })),
    // roadside wreckage — rusted cars, bent street signs, rocks, robot remains
    props: Array.from({ length: 12 }, (_, i) => {
      const kinds = ["car", "sign", "rock", "bot", "rock", "sign"];
      const kind = kinds[i % kinds.length];
      return { x: i * 105 + Math.random() * 50, kind, w: 18 + Math.random() * 20, h: 10 + Math.random() * 14, tilt: (Math.random() - 0.5) * 0.3 };
    }),
    nextObstacle: 80,
    spriteReady: false,
    delivery: 1,
    deliveryFlash: 0,
    runFrames: 0,
    bossFlash: 0,
    bossShatters: 0,
    wormBoss: null,
    wormBossKills: 0,
    bossFights: 0,
    bossVariantKills: [0, 0, 0, 0, 0],
    bossShocks: [],
    bossEngageFlash: 0,
    bossKillFlash: 0,
    bossKillName: "",
    bossDying: null,
    crits: [],
    weakCritLanded: false,
    bountyFlash: 0,
    weaponLevel: 1,
    weaponFlash: 0,
    weaponCrates: [],
    nextCrate: 300,
    weaponBoost: 0,
    crateFlash: 0,
    ranged: "boombox",
    melee: "sword",
    owned: ["boombox", "sword"],
    weaponDrops: [],
    nextDrop: 340,
    dropFlash: 0,
    dropName: "",
    booms: [],
    laserSlash: false,
    laserSwings: 0,
  };
}

/* the laser sword's energy blade stretches the more it gets swung — reach grows with use, capped */
export const laserReach = (g) => 95 + Math.min(g.laserSwings || 0, 30) * 3;
